/**
 * ExplainPanel — right-side drawer that explains a widget or anomaly.
 *
 * V3 Page 01 (2026-04-22). Posts to `/api/dashboard/explain` with the
 * widget spec + rendered data; renders the envelope narrative + drilldowns
 * + action buttons. On md+ it floats as a side-panel; below md it slides
 * up as a sheet. Keyboard: Esc closes. The parent owns `open` / `target`.
 *
 * Non-destructive: wraps children with focus trap only while open; never
 * steals focus from the rest of the page when closed.
 */

import { useEffect, useState } from 'react'
import { X } from 'lucide-react'

type RecommendIntent = 'chat' | 'save_canvas' | 'navigate'

interface Drilldown {
  tool: string
  label: string
  args?: Record<string, unknown>
}

interface RecommendAction {
  label: string
  intent: RecommendIntent
  prompt?: string
  widget_id?: string | null
  route?: string
}

interface ExplainEnvelope {
  headline: string
  /** Plain-text paragraphs. NEVER rendered via dangerouslySetInnerHTML —
   *  the specialist validates this server-side as a string array so there
   *  is no HTML-injection path. */
  narrative: string[]
  drilldowns: Drilldown[]
  recommend_actions: RecommendAction[]
}

interface ExplainResponse {
  ok?: boolean
  source?: 'llm' | 'fallback'
  envelope?: ExplainEnvelope
  llm_error?: string
  error?: string
}

export interface ExplainPanelTarget {
  /** Widget source tag — drives drilldown fallback + headline. */
  widget: {
    id?: string
    kind: string
    label?: string
    [key: string]: unknown
  }
  /** Pre-computed data snapshot the widget is currently rendering. */
  data?: Record<string, unknown>
  /** Optional pre-filled envelope (e.g. anomaly chips pass their payload). */
  prefilled?: ExplainEnvelope
}

export interface ExplainPanelProps {
  open: boolean
  onClose: () => void
  target: ExplainPanelTarget | null
  eventId: number | null
  authToken?: string | null
  /** Router navigator used by "navigate" recommend actions. */
  onNavigate?: (route: string) => void
  /** "Ask Butler Buddy" handler — parent posts the prompt into chat. */
  onAskChat?: (prompt: string) => void
  /** "Save as canvas" handler — parent opens a canvas-save dialog. */
  onSaveCanvas?: (widgetId: string | null) => void
}

export function ExplainPanel({
  open,
  onClose,
  target,
  eventId,
  authToken,
  onNavigate,
  onAskChat,
  onSaveCanvas,
}: ExplainPanelProps) {
  const [state, setState] = useState<
    | { phase: 'idle' }
    | { phase: 'loading' }
    | { phase: 'error'; message: string }
    | { phase: 'ready'; payload: ExplainResponse }
  >({ phase: 'idle' })
  const [requestId, setRequestId] = useState(0)

  // Esc closes. Attach only while open so other modals aren't confused.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !target) {
      setState({ phase: 'idle' })
      return
    }
    if (target.prefilled) {
      setState({
        phase: 'ready',
        payload: { ok: true, source: 'fallback', envelope: target.prefilled },
      })
      return
    }
    setState({ phase: 'loading' })
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (authToken) headers.Authorization = `Bearer ${authToken}`
    const controller = new AbortController()
    const myRequestId = requestId + 1
    setRequestId(myRequestId)
    fetch('/api/dashboard/explain', {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      signal: controller.signal,
      body: JSON.stringify({
        widget: target.widget,
        data: target.data || {},
        event_id: eventId ?? null,
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return (await res.json()) as ExplainResponse
      })
      .then((payload) => {
        if (!payload || !payload.envelope) {
          throw new Error('envelope_missing')
        }
        setState({ phase: 'ready', payload })
      })
      .catch((err) => {
        if (err && (err as { name?: string }).name === 'AbortError') return
        setState({
          phase: 'error',
          message: err instanceof Error ? err.message : 'failed to explain',
        })
      })
    return () => controller.abort()
    // requestId bump drives retry; disable exhaustive-deps lint for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target, eventId, authToken])

  if (!open) return null

  return (
    <aside
      data-testid="explain-panel"
      role="dialog"
      aria-modal="true"
      aria-label="Explain this chart"
      className={[
        'fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-hairline bg-white shadow-2xl',
        'md:w-[420px] md:translate-x-0',
        'dark:border-slate-700 dark:bg-slate-900',
      ].join(' ')}
    >
      <header className="flex items-center justify-between border-b border-hairline px-4 py-3 dark:border-slate-700">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-butler-600 dark:text-butler-200">
          Explain this chart
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-ink-soft hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-400 rounded-full"
          aria-label="Close panel"
          data-testid="explain-panel-close"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      <div
        className="flex-1 overflow-y-auto px-4 py-4 text-sm"
        aria-live="polite"
        aria-busy={state.phase === 'loading' ? 'true' : 'false'}
      >
        {state.phase === 'idle' && (
          <p className="text-ink-soft dark:text-slate-400">Select a chart to explain.</p>
        )}
        {state.phase === 'loading' && (
          <div data-testid="explain-panel-loading" className="space-y-3">
            <div className="h-4 w-40 animate-pulse rounded bg-hairline dark:bg-slate-700" />
            <div className="h-3 w-full animate-pulse rounded bg-hairline dark:bg-slate-700" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-hairline dark:bg-slate-700" />
          </div>
        )}
        {state.phase === 'error' && (
          <div
            data-testid="explain-panel-error"
            className="rounded-card border border-rose-200 bg-rose-50 p-3 text-rose-800 dark:border-rose-700 dark:bg-rose-900/40 dark:text-rose-100"
          >
            <p className="font-semibold">Couldn&apos;t reach the explanation model.</p>
            <p className="mt-1 text-xs">{state.message}</p>
            <button
              type="button"
              onClick={() => {
                setState({ phase: 'idle' })
                setRequestId((n) => n + 1)
              }}
              className="mt-2 inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white px-3 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-100"
              data-testid="explain-panel-retry"
            >
              Try again
            </button>
          </div>
        )}
        {state.phase === 'ready' && state.payload.envelope && (
          <ExplainEnvelopeView
            envelope={state.payload.envelope}
            source={state.payload.source}
            onAskChat={onAskChat}
            onSaveCanvas={onSaveCanvas}
            onNavigate={onNavigate}
          />
        )}
      </div>
    </aside>
  )
}

function ExplainEnvelopeView({
  envelope,
  source,
  onAskChat,
  onSaveCanvas,
  onNavigate,
}: {
  envelope: ExplainEnvelope
  source?: string
  onAskChat?: (prompt: string) => void
  onSaveCanvas?: (widgetId: string | null) => void
  onNavigate?: (route: string) => void
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-butler-800 dark:text-butler-100">
        {envelope.headline}
      </h3>
      <div className="space-y-2 text-ink dark:text-slate-200">
        {envelope.narrative.map((para, i) => (
          <p
            key={i}
            data-testid={`explain-panel-narrative-${i}`}
            className="text-sm leading-relaxed"
          >
            {para}
          </p>
        ))}
      </div>
      {envelope.drilldowns.length > 0 && (
        <section aria-label="Drilldowns" data-testid="explain-panel-drilldowns">
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft dark:text-slate-400">
            Drill deeper
          </h4>
          <ul className="mt-2 space-y-2">
            {envelope.drilldowns.map((d, i) => (
              <li key={`${d.tool}-${i}`}>
                <button
                  type="button"
                  onClick={() => onAskChat?.(`${d.label} — using ${d.tool}`)}
                  className="w-full rounded-card border border-hairline bg-white px-3 py-2 text-left text-sm hover:border-butler-300 hover:bg-butler-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
                >
                  <span className="block font-medium text-ink dark:text-slate-100">{d.label}</span>
                  <span className="mt-0.5 block text-[11px] font-mono text-ink-soft dark:text-slate-400">
                    {d.tool}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {envelope.recommend_actions.length > 0 && (
        <section aria-label="Actions" data-testid="explain-panel-actions" className="flex flex-wrap gap-2">
          {envelope.recommend_actions.map((action, i) => (
            <button
              key={`${action.intent}-${i}`}
              type="button"
              onClick={() => {
                if (action.intent === 'chat') {
                  onAskChat?.(action.prompt || envelope.headline)
                } else if (action.intent === 'save_canvas') {
                  onSaveCanvas?.(action.widget_id ?? null)
                } else if (action.intent === 'navigate' && action.route) {
                  onNavigate?.(action.route)
                }
              }}
              className="inline-flex items-center gap-1 rounded-full border border-butler-300 bg-butler-50 px-3 py-1 text-xs font-semibold text-butler-800 hover:bg-butler-100 dark:border-butler-700 dark:bg-butler-900/40 dark:text-butler-100"
            >
              {action.label}
            </button>
          ))}
        </section>
      )}
      {source === 'fallback' && (
        <p
          data-testid="explain-panel-source-fallback"
          className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-soft dark:text-slate-400"
        >
          Deterministic preview · live narrative unavailable
        </p>
      )}
    </div>
  )
}
